import type React from 'react';
import { useEffect } from 'react';
import { CheckIcon, CloseIcon, MessageIcon, WrenchIcon } from '../../Icons';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { removeToast } from '../../store';

export const ToastContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const toasts = useAppSelector((state) => state.ui.toasts);

  return (
    <div className="fixed bottom-20 right-4 z-50 space-y-2 max-w-sm w-full sm:bottom-6">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => dispatch(removeToast(toast.id))} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: {
    id: string;
    type?: 'success' | 'error' | 'warning' | 'info';
    message: string;
    duration?: number;
  };
  onRemove: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove();
    }, toast.duration || 5000);

    return () => clearTimeout(timer);
  }, [toast.duration, onRemove]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckIcon size={20} />;
      case 'error':
        return <CloseIcon size={20} />;
      case 'warning':
        return <WrenchIcon size={20} />;
      default:
        return <MessageIcon size={20} />;
    }
  };

  const getStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'error':
        return 'bg-red-50 text-red-800 border-red-200';
      case 'warning':
        return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      default:
        return 'bg-blue-50 text-blue-800 border-blue-200';
    }
  };

  const getIconStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      case 'warning':
        return 'text-yellow-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div
      className={`
        flex items-center gap-3 p-4 rounded-lg border shadow-lg backdrop-blur-sm
        transform transition-all duration-300 ease-in-out
        ${getStyles()}
      `}
      style={{
        animation: 'slideInRight 0.3s ease-out forwards',
      }}
    >
      <div className={`flex-shrink-0 ${getIconStyles()}`}>{getIcon()}</div>
      <p className="flex-1 text-sm font-medium">{toast.message}</p>
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );
};

// Add CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
document.head.appendChild(style);
